import { useEffect, useRef, useState } from 'react';
import { OFF } from '../../lib/off';
import { Link, useNavigate } from 'react-router';
import { formatPhoneNumber } from '@stitch/pbx-adapter/catalog';
import { lookup } from '@stitch/pbx-adapter/describe';
import { api, type E911Address, type NumberOrder } from '../../api/client';
import { AddressChooser, AddressFields, EMPTY_ADDRESS, ORDER_STATUS, OrderLines, addressErrors, choiceOf, draftAddress, draftErrors, draftFor, isTollFree, oneLine, type AddressDraft } from '../../components/numbers';
import { NewNumbersGoTo } from '../../components/NewNumbersGoTo';
import { useTenant } from '../../components/Shell';
import { Button, Card, Chip, Field, Loading, Notice, PageHeader } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { dateOnly, plural } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { useData } from '../../lib/useData';

const STEPS = [
  { id: 'numbers', title: 'Numbers' },
  { id: 'account', title: 'Current provider' },
  { id: 'arrive', title: 'When they arrive' },
  { id: 'sign', title: 'Sign' }
] as const;
type StepId = (typeof STEPS)[number]['id'];

/** "410-555-0100", "(410) 555 0100", "14105550100" → "+14105550100"; anything else as typed. */
export function toE164(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return raw.trim();
}

const MAX_BILL = 5 * 1024 * 1024;
/** A move takes a couple of weeks; earlier dates are moved to the first one the carriers allow. */
const daysFromNow = (n: number) => {
  const t = new Date(Date.now() + n * 86_400_000);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};

interface Account {
  accountName: string;
  businessName: string;
  firstName: string;
  lastName: string;
  btn: string;
  accountNumber: string;
  pin: string;
  serviceAddress: E911Address;
}

/** Moving numbers in from another provider (a port): which numbers, their account there, a signature. */
export function MoveNumbers() {
  const { tenant } = useTenant();
  const d = tenant.domain;
  const base = `/t/${encodeURIComponent(d)}`;
  const navigate = useNavigate();
  const { state } = useAuth();
  const status = useData(() => api.carrierStatus(d), [d]);
  const e911 = useData(() => api.e911(d).catch(() => undefined), [d]);
  const dir = useData(() => api.directory(d).catch(() => undefined), [d]);

  const [step, setStep] = useState<StepId>('numbers');
  const [typed, setTyped] = useState('');
  const [check, setCheck] = useState<{ portable: string[]; notPortable: { number: string; reason: string }[] } | null>(null);
  const [checking, setChecking] = useState(false);
  const [a, setA] = useState<Account>(() => {
    const [first = '', ...rest] = (state?.user?.name ?? '').split(' ');
    return { accountName: tenant.name, businessName: '', firstName: first, lastName: rest.join(' '), btn: '', accountNumber: '', pin: '', serviceAddress: { ...EMPTY_ADDRESS } };
  });
  const [destination, setDestination] = useState('');
  const [waiting, setWaiting] = useState(false);
  const inInventory = waiting && Boolean(status.data?.inventory);
  const [address, setAddress] = useState<AddressDraft | null>(null);
  const [wanted, setWanted] = useState(daysFromNow(14));
  const [signature, setSignature] = useState('');
  const [bill, setBill] = useState<{ name: string; dataUrl: string } | null>(null);
  const [billError, setBillError] = useState('');
  const [authorize, setAuthorize] = useState(false);
  const [agreeAddress, setAgreeAddress] = useState(false);
  const [tried, setTried] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [order, setOrder] = useState<NumberOrder | null>(null);

  const saved = e911.data?.addresses ?? [];
  useEffect(() => {
    if (e911.data && !address) setAddress(draftFor(e911.data.addresses));
  }, [e911.data, address]);
  const draft = address ?? { mode: 'none' as const };
  const portable = check?.portable ?? [];
  const allTollFree = portable.length > 0 && portable.every(isTollFree);
  const index = STEPS.findIndex((s) => s.id === step);
  const set = (p: Partial<Account>) => setA((x) => ({ ...x, ...p }));

  const accountErrors = (): Partial<Record<keyof Account | keyof E911Address, string>> => {
    const e: Partial<Record<keyof Account | keyof E911Address, string>> = { ...addressErrors(a.serviceAddress) };
    if (a.accountName.trim().length < 2) e.accountName = 'The name on the account, as on the bill.';
    if (!a.firstName.trim()) e.firstName = 'Enter a first name.';
    if (!a.lastName.trim()) e.lastName = 'Enter a last name.';
    if (!/^\+1\d{10}$/.test(toE164(a.btn))) e.btn = 'The account’s main number, like (410) 555-0100.';
    if (a.accountNumber.trim().length < 2) e.accountNumber = 'As on the bill.';
    return e;
  };

  function problem(id: StepId): string {
    if (id === 'numbers') return portable.length ? '' : check ? 'None of these can be moved here.' : 'Check the numbers first.';
    if (id === 'account') return Object.keys(accountErrors()).length ? 'Fill in the account details.' : '';
    if (id === 'arrive') {
      if (!destination && !inInventory) return 'Choose where calls go.';
      if (!allTollFree && Object.keys(draftErrors(draft)).length) return 'Check the 911 address.';
      if (!wanted || wanted < daysFromNow(5)) return 'Choose a date at least 5 days away.';
      return '';
    }
    if (!signature) return 'Sign in the box.';
    if (!authorize) return 'Confirm you’re allowed to move these numbers.';
    if (!allTollFree && draft.mode !== 'none' && !agreeAddress) return 'Confirm the 911 address is where people using the numbers will be.';
    return '';
  }

  function goTo(id: StepId) {
    const target = STEPS.findIndex((s) => s.id === id);
    for (let i = 0; i < target; i++) {
      if (problem(STEPS[i]!.id)) {
        setTried(true);
        setStep(STEPS[i]!.id);
        return;
      }
    }
    setTried(false);
    setStep(id);
  }

  async function runCheck() {
    const list = [...new Set(typed.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean).map(toE164))];
    const bad = list.filter((n) => !/^\+\d{8,15}$/.test(n));
    if (!list.length || bad.length) {
      setCheck(null);
      setError(bad.length ? `These don’t look like phone numbers: ${bad.join(', ')}` : 'Enter the numbers to move, one per line.');
      return;
    }
    setError('');
    setChecking(true);
    try {
      const r = await api.portCheck(d, list);
      setCheck(r);
      if (!a.btn && r.portable[0]) set({ btn: formatPhoneNumber(r.portable[0]) });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setChecking(false);
    }
  }

  async function pickBill(file: File | undefined) {
    setBillError('');
    if (!file) return setBill(null);
    if (!/^(application\/pdf|image\/(png|jpeg))$/.test(file.type)) return setBillError('A PDF, PNG or JPEG, please.');
    if (file.size > MAX_BILL) return setBillError('Up to 5 MB, please.');
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = () => reject(r.error);
      r.readAsDataURL(file);
    });
    setBill({ name: file.name, dataUrl });
  }

  async function submit() {
    setTried(true);
    // What's missing shows next to it, on the first step that needs something.
    const bad = STEPS.find((x) => problem(x.id));
    if (bad) {
      setError('');
      setStep(bad.id);
      return;
    }
    setSending(true);
    setError('');
    try {
      const r = await api.portIn(d, {
        numbers: portable,
        accountName: a.accountName.trim(),
        ...(a.businessName.trim() ? { businessName: a.businessName.trim() } : {}),
        firstName: a.firstName.trim(),
        lastName: a.lastName.trim(),
        serviceAddress: { ...a.serviceAddress, address1: a.serviceAddress.address1.trim(), address2: a.serviceAddress.address2.trim(), city: a.serviceAddress.city.trim(), zip: a.serviceAddress.zip.trim() },
        btn: toE164(a.btn),
        accountNumber: a.accountNumber.trim(),
        ...(a.pin.trim() ? { pin: a.pin.trim() } : {}),
        wantedDate: wanted,
        signature,
        ...(bill ? { bill } : {}),
        destination: inInventory ? '' : destination,
        address: allTollFree ? null : choiceOf(draft),
        authorize: true,
        ...(!allTollFree && draft.mode !== 'none' ? { confirmAddress: agreeAddress } : {})
      });
      setOrder(r.order);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }

  const header = <PageHeader eyebrow={<Link to={`${base}/numbers`}>Numbers</Link>} title="Move numbers to Stitch" />;
  if (!status.data || (!e911.data && e911.loading)) return <>{header}<Loading label="Loading" /></>;
  if (!status.data.carrier || !status.data.writes || !status.data.routed)
    return (
      <>
        {header}
        <div className="content">
          <Notice icon="lock" title={OFF.movingOff}>
            Stitch support can move your numbers for you: send us a message from the Help button with the numbers and a recent bill from your current provider.
          </Notice>
        </div>
      </>
    );

  if (order) {
    const s = ORDER_STATUS[order.status];
    return (
      <>
        <PageHeader eyebrow={<Link to={`${base}/numbers`}>Numbers</Link>} title="Your numbers are on their way" chips={<Chip tone={s.tone}>{s.label}</Chip>} />
        <div className="content">
          <Card>
            <div className="card-body section-stack">
              <p className="wizard-lede">
                We’ve asked for {order.lines.length === 1 ? 'the number' : `all ${order.lines.length} numbers`} to move on {order.port ? dateOnly(`${order.port.wantedDate}T12:00:00`) : 'the date you chose'}. Your current provider usually confirms a date within a few days, and we’ll show it on Numbers.
                Keep your current service until the move: the numbers work there until then, and with you here straight after.
              </p>
              <OrderLines order={order} />
              <div className="wizard-actions">
                <Link className="btn primary" to={`${base}/numbers`}>
                  Back to Numbers
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </>
    );
  }

  const errs = tried ? accountErrors() : {};
  const chosen = draftAddress(draft, saved);
  return (
    <>
      {header}
      <div className="content">
        {!tenant.writable && <Notice icon="lock">{d} is read-only in this environment, so a move can be prepared here but not sent.</Notice>}
        <div className="wizard-layout">
          <nav className="wizard-steps" aria-label="Steps">
            <ol>
              {STEPS.map((s, i) => (
                <li key={s.id} className={s.id === step ? 'current' : i < index ? 'past' : undefined}>
                  <button type="button" onClick={() => goTo(s.id)} aria-current={s.id === step ? 'step' : undefined}>
                    <span className="wizard-num" aria-hidden="true">
                      {i < index ? <Icon name="check" size={13} strokeWidth={2.6} /> : i + 1}
                    </span>
                    {s.title}
                  </button>
                </li>
              ))}
            </ol>
          </nav>
          <Card title={STEPS[index]!.title}>
            <form
              className="card-body wizard-body"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                const next = STEPS[index + 1];
                if (next) goTo(next.id);
                else void submit();
              }}
            >
              {step === 'numbers' && (
                <>
                  <p className="wizard-lede">Bring the numbers your company has now with another phone company. Callers keep dialing the same numbers.</p>
                  <div className="field">
                    <label htmlFor="mv-numbers">Numbers to move</label>
                    <textarea
                      id="mv-numbers"
                      className="input textarea mono"
                      rows={5}
                      value={typed}
                      onChange={(e) => (setTyped(e.target.value), setCheck(null))}
                      placeholder={'(410) 555-0100\n(410) 555-0101'}
                      autoFocus
                    />
                    <span className="hint">One per line. Up to 100 at a time; numbers on different accounts go in separate requests.</span>
                  </div>
                  <div>
                    <Button variant={check ? 'secondary' : 'primary'} icon="check" onClick={() => void runCheck()} disabled={checking}>
                      {checking ? 'Checking…' : 'Check they can move'}
                    </Button>
                  </div>
                  {check && (
                    <ul className="plain-list port-check">
                      {check.portable.map((n) => (
                        <li key={n}>
                          <Icon name="check" size={15} strokeWidth={2.4} /> <span className="mono">{formatPhoneNumber(n)}</span> <span className="muted">can move</span>
                        </li>
                      ))}
                      {check.notPortable.map((n) => (
                        <li key={n.number} className="bad">
                          <Icon name="x" size={15} strokeWidth={2.4} /> <span className="mono">{formatPhoneNumber(n.number)}</span> <span className="muted">{n.reason}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {check && check.notPortable.length > 0 && check.portable.length > 0 && <span className="hint">The ones that can’t move are left out. Stitch support can tell you more about them.</span>}
                  {error && step === 'numbers' && <span className="error">{error}</span>}
                  {tried && !error && problem('numbers') && <span className="error">{problem('numbers')}</span>}
                </>
              )}

              {step === 'account' && (
                <>
                  <p className="wizard-lede">Exactly as your current provider has them: the move is refused if a detail doesn’t match. A recent bill shows them all.</p>
                  <div className="form-grid">
                    <Field label="Name on the account" value={a.accountName} onChange={(e) => set({ accountName: e.target.value })} error={errs.accountName} />
                    <Field label="Business name" value={a.businessName} onChange={(e) => set({ businessName: e.target.value })} hint="If it’s different. Optional." />
                  </div>
                  <div className="form-grid">
                    <Field label="Contact first name" value={a.firstName} onChange={(e) => set({ firstName: e.target.value })} error={errs.firstName} hint="Someone allowed to make changes to the account." />
                    <Field label="Contact last name" value={a.lastName} onChange={(e) => set({ lastName: e.target.value })} error={errs.lastName} />
                  </div>
                  <div className="form-grid">
                    <Field label="Account number" value={a.accountNumber} onChange={(e) => set({ accountNumber: e.target.value })} error={errs.accountNumber} autoComplete="off" />
                    <Field label="Account’s main number" type="tel" value={a.btn} onChange={(e) => set({ btn: e.target.value })} error={errs.btn} hint="The billing telephone number (BTN)." />
                    <Field label="PIN or passcode" value={a.pin} onChange={(e) => set({ pin: e.target.value })} hint="If the account has one. Mobile numbers always do." autoComplete="off" />
                  </div>
                  <span className="field-label">Service address on the account</span>
                  <AddressFields value={a.serviceAddress} onChange={(serviceAddress) => set({ serviceAddress })} errors={errs} />
                  <p className="muted small-print">
                    <Icon name="lock" size={13} /> Stitch passes the account number and PIN to the carrier for the move and doesn’t keep them.
                  </p>
                </>
              )}

              {step === 'arrive' && (
                <>
                  <NewNumbersGoTo
                    inventory={Boolean(status.data?.inventory)}
                    waiting={inInventory}
                    onWaiting={setWaiting}
                    destination={destination}
                    onDestination={setDestination}
                    dir={dir.data}
                    company={tenant.name}
                    many={portable.length !== 1}
                    error={tried && !destination && !inInventory ? 'Choose where calls go.' : undefined}
                  />
                  {!allTollFree && (
                    <div className="field">
                      <span className="field-label">911 address</span>
                      <span className="hint">Where people using the numbers are, for emergency services. Set once they arrive.</span>
                      {a.serviceAddress.address1 && (draft.mode !== 'new' || draft.address.address1 !== a.serviceAddress.address1) && (
                        <div>
                          <button type="button" className="link-btn" onClick={() => setAddress({ mode: 'new', address: { ...a.serviceAddress }, save: saved.length === 0, label: saved.length === 0 ? 'Main office' : '' })}>
                            Use the service address
                          </button>
                        </div>
                      )}
                      <AddressChooser saved={saved} value={draft} onChange={setAddress} showErrors={tried} none="Set it on Emergency addresses after the numbers arrive." />
                    </div>
                  )}
                  <Field
                    label="Move on"
                    type="date"
                    min={daysFromNow(5)}
                    value={wanted}
                    onChange={(e) => setWanted(e.target.value)}
                    error={tried && (!wanted || wanted < daysFromNow(5)) ? 'At least 5 days away.' : undefined}
                    hint="The date you’d like. Moves usually take 1 to 2 weeks; your current provider confirms the date."
                  />
                </>
              )}

              {step === 'sign' && (
                <>
                  <div className="loa">
                    <strong>Letter of authorization</strong>
                    <p>
                      I authorize Stitch and its carrier to act as {a.accountName.trim() || 'the account holder'}’s agent to move {plural(portable.length, 'phone number')}, {portable.map(formatPhoneNumber).join(', ')}, from our current
                      provider, account {a.accountNumber.trim() ? `ending ${a.accountNumber.trim().slice(-4)}` : '(as given)'}, for service at {oneLine(a.serviceAddress) || 'the service address given'}. I understand our service with the
                      current provider for these numbers ends when they move, and that we remain responsible for any amount owed to them.
                    </p>
                  </div>
                  <SignaturePad value={signature} onChange={setSignature} name={`${a.firstName} ${a.lastName}`.trim()} />
                  {tried && !signature && <span className="error">Sign in the box.</span>}
                  <div className="field">
                    <label htmlFor="mv-bill">A recent bill</label>
                    <input id="mv-bill" type="file" accept="application/pdf,image/png,image/jpeg" onChange={(e) => void pickBill(e.target.files?.[0])} />
                    <span className="hint">{bill ? `Attached: ${bill.name}` : 'Optional, but moves with a bill go through first time far more often. PDF or picture, up to 5 MB.'}</span>
                    {billError && <span className="error">{billError}</span>}
                  </div>
                  <label className="check confirm-check">
                    <input type="checkbox" checked={authorize} onChange={(e) => setAuthorize(e.target.checked)} />
                    <span>I’m allowed to move these numbers for {a.accountName.trim() || 'this account'}, and the details I’ve given are correct.</span>
                  </label>
                  {tried && !authorize && <span className="error">Confirm you’re allowed to move these numbers.</span>}
                  {!allTollFree && chosen && (
                    <label className="check confirm-check">
                      <input type="checkbox" checked={agreeAddress} onChange={(e) => setAgreeAddress(e.target.checked)} />
                      <span>
                        People who call out with these numbers will be at <strong>{oneLine(chosen)}</strong>, for 911.
                      </span>
                    </label>
                  )}
                  {tried && authorize && signature && problem('sign') && <span className="error">{problem('sign')}</span>}
                  <dl className="dl">
                    <dt>Calls go to</dt>
                    <dd>{inInventory ? 'Open inventory, for now' : destination ? lookup(dir.data, destination).name : '—'}</dd>
                    <dt>911 address</dt>
                    <dd>{allTollFree ? 'Not needed (toll-free)' : chosen ? oneLine(chosen) : <Chip small tone="warn">Set it after they arrive</Chip>}</dd>
                    <dt>Move on</dt>
                    <dd>{wanted ? dateOnly(`${wanted}T12:00:00`) : '—'}</dd>
                  </dl>
                  {error && <Notice tone="crit">{error}</Notice>}
                </>
              )}

              <div className="wizard-actions">
                {index > 0 && (
                  <Button variant="ghost" onClick={() => goTo(STEPS[index - 1]!.id)}>
                    Back
                  </Button>
                )}
                {step !== 'sign' ? (
                  <Button variant="primary" type="submit">
                    Next: {STEPS[index + 1]!.title}
                  </Button>
                ) : (
                  <Button variant="primary" type="submit" icon="truck" disabled={sending || !tenant.writable}>
                    {sending ? 'Sending…' : `Ask to move ${plural(portable.length, 'number')}`}
                  </Button>
                )}
                <Button variant="ghost" onClick={() => navigate(`${base}/numbers`)}>
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </>
  );
}

/** Signing with a finger or the mouse; the PNG goes with the request. */
export function SignaturePad({ value, onChange, name }: { value: string; onChange: (png: string) => void; name: string }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const drawn = useRef(false);

  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    // Sharp on high-density screens.
    const ratio = window.devicePixelRatio || 1;
    const { width, height } = c.getBoundingClientRect();
    c.width = Math.round(width * ratio);
    c.height = Math.round(height * ratio);
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111';
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, width, height);
      img.src = value;
      drawn.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const ctx = () => canvas.current?.getContext('2d') ?? null;

  return (
    <div className="field">
      <span className="field-label" id="sig-label">
        Signature{name ? ` of ${name}` : ''}
      </span>
      <div className="signature-pad">
        <canvas
          ref={canvas}
          role="img"
          aria-labelledby="sig-label"
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            drawing.current = true;
            const p = point(e);
            const c = ctx();
            c?.beginPath();
            c?.moveTo(p.x, p.y);
            c?.lineTo(p.x + 0.1, p.y + 0.1);
            c?.stroke();
          }}
          onPointerMove={(e) => {
            if (!drawing.current) return;
            const p = point(e);
            const c = ctx();
            c?.lineTo(p.x, p.y);
            c?.stroke();
            drawn.current = true;
          }}
          onPointerUp={() => {
            drawing.current = false;
            if (drawn.current && canvas.current) onChange(canvas.current.toDataURL('image/png'));
          }}
          onPointerCancel={() => (drawing.current = false)}
        />
        {!value && <span className="signature-hint">Sign here</span>}
      </div>
      <div>
        <button
          type="button"
          className="link-btn"
          disabled={!value}
          onClick={() => {
            const c = canvas.current;
            if (c) c.getContext('2d')?.clearRect(0, 0, c.width, c.height);
            drawn.current = false;
            onChange('');
          }}
        >
          Clear
        </button>
      </div>
    </div>
  );
}
